import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detecta que há uma build nova publicada e aplica a atualização.
 *
 * Mesmo mecanismo do innova-admin: polling de `/version.json` comparado com a
 * versão embutida no bundle, com checagem imediata ao voltar para o foreground.
 * Ver docs/07-mobile-e-pwa.md §11.
 *
 * Sem service worker no caminho (docs/07 §8), `location.reload()` basta: o
 * navegador vai à rede, o CDN serve o index.html novo (`no-cache`) e os assets
 * novos vêm pelos hashes. Toda a coreografia anterior de `skipWaiting` /
 * `controllerchange` saiu junto com o Workbox.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Em teste e em dev o `define` do Vite não roda; o fallback evita ReferenceError. */
const CURRENT_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

interface VersionPayload {
  version: string;
}

interface UseAppVersion {
  updateAvailable: boolean;
  applyUpdate: () => void;
  dismissUpdate: () => void;
  currentVersion: string;
}

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    // O query param derrota cache intermediário que ignore o no-store.
    const res = await fetch(`/version.json?t=${String(Date.now())}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (!res.ok) return null;

    const data = (await res.json()) as VersionPayload | null;

    return typeof data?.version === 'string' ? data.version : null;
  } catch {
    // Falha de rede não é motivo para pedir atualização.
    return null;
  }
}

export function useAppVersion(): UseAppVersion {
  const [updateDetected, setUpdateDetected] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const stoppedRef = useRef(false);

  useEffect(() => {
    // Em dev o version.json não existe.
    if (import.meta.env.DEV) return undefined;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      if (stoppedRef.current) return;

      // Aba em background não consome rede nem bateria à toa.
      if (document.visibilityState !== 'visible') {
        timeoutId = setTimeout(() => {
          void tick();
        }, POLL_INTERVAL_MS);
        return;
      }

      const remote = await fetchRemoteVersion();

      if (remote && remote !== CURRENT_VERSION) {
        setUpdateDetected(true);
        // Achou: para de perguntar. O aviso já está na tela.
        stoppedRef.current = true;
        return;
      }

      timeoutId = setTimeout(() => {
        void tick();
      }, POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !stoppedRef.current) {
        if (timeoutId) clearTimeout(timeoutId);
        void tick();
      }
    };

    timeoutId = setTimeout(() => {
      void tick();
    }, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stoppedRef.current = true;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => {
    setIsDismissed(true);
  }, []);

  return {
    updateAvailable: updateDetected && !isDismissed,
    applyUpdate,
    dismissUpdate,
    currentVersion: CURRENT_VERSION,
  };
}
